import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import React from 'react';

export default function Dropdown<TKey>({
  options,
  value,
  onChange,
  alternative,
  txHistory,
  openOnHover,
  isSolid,
  isSelectionListed,
  ...props
}: {
  options: {
    key: TKey;
    name: string;
  }[];
  value: TKey;
  alternative?: boolean;
  txHistory?: boolean;
  onChange: (value: TKey) => void;
  [props: string]: unknown;
  openOnHover?: boolean;
  isSolid?: boolean;
  isSelectionListed?: boolean;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const isMountedRef = React.useRef(true);

  const filterOptions = options?.filter(option => option?.key !== value);

  const filteredOptions = isSelectionListed ? filterOptions : options;

  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const arrowColor = alternative ? '#FFFFFF' : '#A4A7C1';
  const backgroundColor = alternative
    ? 'rgba(255,255,255,0.16)'
    : 'var(--input-background)';
  const txHistoryBackgroundColor = 'var(--tx-history-background)';

  return (
    <div
      className={clsx('relative inline-block max-w-full', props, {
        'min-w-90 lg:min-w-120': isSolid,
      })}>
      <div>
        <span className="w-full">
          <button
            onMouseDown={() => setIsOpen(!isOpen)}
            onMouseEnter={() => openOnHover && setIsOpen(true)}
            onMouseLeave={() => openOnHover && setIsOpen(false)}
            onBlur={() => {
              if (isMountedRef.current) {
                setIsOpen(false);
              }
            }}
            type="button"
            style={{
              backgroundColor: txHistory
                ? txHistoryBackgroundColor
                : backgroundColor,
            }}
            className={clsx(
              'h-12 inline-flex w-full items-center px-3 md:px-4 bg-input-background text-sm leading-5 font-semibold text-header-text focus:outline-none transition ease-in-out duration-150 py-2',
              {
                'rounded py-1 justify-start': txHistory,
                'rounded-full justify-start': !txHistory && !isSolid,
                'rounded-lg': (isSolid && !isOpen) || options?.length <= 1,
                'justify-center cursor-auto': options?.length <= 1,
                'justify-between': options?.length > 1,
                'rounded-tl-lg rounded-tr-lg justify-between':
                  isSolid && isOpen && options?.length > 1,
              },
            )}
            id="options-menu"
            aria-haspopup="true"
            aria-expanded="true">
            <div
              className={clsx('whitespace-nowrap truncate', {
                'mr-2 md:mr-4': options?.length > 1,
              })}>
              {options?.find(option => option.key === value)?.name}
            </div>
            {options?.length > 1 && (
              <svg
                className="right-2 lg:right-1"
                width="8"
                height="8"
                viewBox="0 0 8 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M3.56699 6.8501C3.75944 7.18343 4.24056 7.18343 4.43301 6.8501L7.89711 0.850098C8.08957 0.516765 7.849 0.100098 7.4641 0.100098H0.535898C0.150998 0.100098 -0.0895646 0.516764 0.102885 0.850098L3.56699 6.8501Z"
                  fill={arrowColor}
                />
              </svg>
            )}
          </button>
        </span>
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={clsx(
              'origin-top absolute z-10 shadow-input-background',
              {
                'mt-2': !isSolid,
                'w-full': isSolid,
              },
            )}
            onMouseEnter={() => openOnHover && setIsOpen(true)}
            onMouseLeave={() => openOnHover && setIsOpen(false)}>
            <div
              className={clsx(' text-center', {
                'ring-1 ring-black ring-opacity-5 rounded-lg': !isSolid,
                'rounded-bl-lg rounded-br-lg': isSolid,
              })}
              style={{
                backgroundColor: txHistory
                  ? txHistoryBackgroundColor
                  : backgroundColor,
                backdropFilter: alternative ? 'blur(10px)' : undefined,
              }}>
              <div
                className={clsx('text-center', {
                  'p-0': isSolid,
                  'p-2': !isSolid,
                })}
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="options-menu">
                {filteredOptions?.map((option, oi) => (
                  <button
                    key={`dropdown-option-${oi}-${option.key}`}
                    type="button"
                    onMouseDown={() => {
                      onChange(option.key);
                      setIsOpen(false);
                    }}
                    className={clsx(
                      'block w-full text-left py-2 text-sm leading-5 focus:outline-none whitespace-nowrap',
                      {
                        'text-body-text hover:text-header-text focus:bg-background':
                          !alternative,
                        'text-white text-opacity-75 hover:text-opacity-100':
                          alternative,
                        'px-4': isSolid,
                        'px-2': !isSolid,
                      },
                    )}
                    role="menuitem">
                    {option.name}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
